"use client"

import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { SortingState } from '@tanstack/react-table'
import type { FilterValues } from '@open-mercato/ui/backend/FilterBar'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody, PageHeader } from '@open-mercato/ui/backend/Page'
import { DynamicTable } from '@open-mercato/ui/backend/DynamicTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { buildCrudQuery, createCrud, deleteCrud, updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { Button } from '@open-mercato/ui/primitives/button'
import { DriverDialog, toDriverFormValues, type DriverMutationPayload } from '../lib/driverForm'
import {
  buildDriversColumns,
  buildDriversFilters,
  type DriversListResponse,
  type DriverListItem,
} from '../lib/driversTable'

const DEFAULT_PAGE_SIZE = 25

function buildDriversListParams(
  page: number,
  search: string,
  filterValues: FilterValues,
  sorting: SortingState,
) {
  const sort = sorting[0]
  return {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    q: search.trim() || undefined,
    qualification: typeof filterValues.qualification === 'string' ? filterValues.qualification : undefined,
    licenseCategory: typeof filterValues.licenseCategory === 'string' ? filterValues.licenseCategory : undefined,
    isActive: typeof filterValues.isActive === 'boolean' ? filterValues.isActive : undefined,
    sortField: sort?.id,
    sortDir: sort?.desc ? 'desc' : 'asc',
  }
}

function normalizeInlineDraft(draft: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(draft)) {
    if (Array.isArray(value)) {
      next[key] = value
      continue
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      next[key] = trimmed.length > 0 ? trimmed : null
      continue
    }
    next[key] = value
  }
  return next
}

async function fetchDriversList(params: ReturnType<typeof buildDriversListParams>): Promise<DriversListResponse> {
  return readApiResultOrThrow<DriversListResponse>(`/api/road/drivers?${buildCrudQuery(params)}`, undefined, {
    errorMessage: 'Failed to fetch drivers.',
  })
}

export default function DriversListPage() {
  const t = useT()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: 'drivers-list' })

  const [page, setPage] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'lastName', desc: false }])
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [createOpen, setCreateOpen] = React.useState(false)
  const [createPending, setCreatePending] = React.useState(false)

  const params = React.useMemo(
    () => buildDriversListParams(page, search, filterValues, sorting),
    [filterValues, page, search, sorting],
  )

  const driversQuery = useQuery({
    queryKey: ['drivers', 'list', scopeVersion, params],
    queryFn: () => fetchDriversList(params),
  })

  const refreshDrivers = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['drivers', 'list'] })
    await queryClient.invalidateQueries({ queryKey: ['drivers', 'detail'] })
  }, [queryClient])

  const handleInlineSave = React.useCallback(async (row: DriverListItem, draft: Record<string, unknown>) => {
    await runMutation({
      context: { kind: 'road.driver', record: { id: row.id } },
      mutationPayload: draft,
      operation: async () => {
        await updateCrud(`road/drivers/${row.id}`, normalizeInlineDraft(draft), {
          errorMessage: t('drivers.messages.updateError', 'Failed to update driver.'),
        })
      },
    })
    flash(t('drivers.messages.updateSuccess', 'Driver updated.'), 'success')
    await refreshDrivers()
  }, [refreshDrivers, runMutation, t])

  const handleActiveToggle = React.useCallback(async (row: DriverListItem, next: boolean) => {
    try {
      await runMutation({
        context: { kind: 'road.driver', record: { id: row.id } },
        mutationPayload: { isActive: next },
        operation: async () => {
          await updateCrud(`road/drivers/${row.id}`, { isActive: next }, {
            errorMessage: t('drivers.messages.updateError', 'Failed to update driver.'),
          })
        },
      })
      flash(t('drivers.messages.updateSuccess', 'Driver updated.'), 'success')
      await refreshDrivers()
    } catch {
      flash(t('drivers.messages.updateError', 'Failed to update driver.'), 'error')
    }
  }, [refreshDrivers, runMutation, t])

  const handleDelete = React.useCallback(async (row: DriverListItem) => {
    const approved = await confirm({
      title: t('drivers.confirm.deleteTitle', 'Delete driver?'),
      text: t('drivers.confirm.deleteMessage', 'This will archive the driver and keep audit history intact.'),
      confirmText: t('drivers.actions.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    try {
      await runMutation({
        context: { kind: 'road.driver', record: { id: row.id } },
        mutationPayload: { id: row.id },
        operation: async () => {
          await deleteCrud(`road/drivers/${row.id}`, {
            errorMessage: t('drivers.messages.deleteError', 'Failed to delete driver.'),
          })
        },
      })
      flash(t('drivers.messages.deleteSuccess', 'Driver deleted.'), 'success')
      await refreshDrivers()
    } catch {
      flash(t('drivers.messages.deleteError', 'Failed to delete driver.'), 'error')
    }
  }, [confirm, refreshDrivers, runMutation, t])

  const columns = React.useMemo(
    () => buildDriversColumns(t, {
      onToggleActive: (row, next) => { void handleActiveToggle(row, next) },
    }),
    [handleActiveToggle, t],
  )

  const filters = React.useMemo(() => buildDriversFilters(t), [t])

  if (driversQuery.isLoading && !driversQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('drivers.pages.list.title', 'Drivers')}
          description={t('drivers.pages.list.description', 'Road driver registry for dispatch and compliance.')}
        />
        <PageBody>
          <LoadingMessage label={t('drivers.messages.loading', 'Loading drivers…')} />
        </PageBody>
      </Page>
    )
  }

  if (driversQuery.isError || !driversQuery.data) {
    return (
      <Page>
        <PageHeader
          title={t('drivers.pages.list.title', 'Drivers')}
          description={t('drivers.pages.list.description', 'Road driver registry for dispatch and compliance.')}
        />
        <PageBody>
          <ErrorMessage
            label={t('drivers.messages.loadErrorTitle', 'Could not load drivers')}
            description={t('drivers.messages.loadError', 'Try again in a moment or refresh the page.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('drivers.pages.list.title', 'Drivers')}
        description={t('drivers.pages.list.description', 'Road driver registry for dispatch and compliance.')}
        actions={(
          <Button type="button" onClick={() => setCreateOpen(true)}>
            {t('drivers.actions.addDriver', 'Add driver')}
          </Button>
        )}
      />
      <PageBody>
        <DynamicTable
          title={t('drivers.pages.list.tableTitle', 'Drivers')}
          columns={columns}
          data={driversQuery.data.items}
          onSaveEdit={handleInlineSave}
          sortable={true}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next)
            setPage(1)
          }}
          pagination={{
            page: driversQuery.data.page,
            pageSize: driversQuery.data.pageSize,
            total: driversQuery.data.total,
            totalPages: driversQuery.data.totalPages,
            onPageChange: setPage,
          }}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          searchPlaceholder={t('drivers.pages.list.searchPlaceholder', 'Search by name, phone, or email')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(next) => {
            setFilterValues(next)
            setPage(1)
          }}
          onFiltersClear={() => {
            setFilterValues({})
            setPage(1)
          }}
          rowActions={(row, helpers) => (
            <RowActions
              items={helpers.isEditing
                ? [
                    {
                      id: 'save',
                      label: helpers.isSaving
                        ? t('drivers.actions.saving', 'Saving…')
                        : t('drivers.actions.save', 'Save'),
                      onSelect: () => { void helpers.saveEdit() },
                    },
                    {
                      id: 'cancel',
                      label: t('drivers.actions.cancelEdit', 'Cancel'),
                      onSelect: helpers.cancelEdit,
                    },
                  ]
                : [
                    {
                      id: 'open',
                      label: t('drivers.actions.viewDetail', 'View detail'),
                      href: `/backend/drivers/${row.id}`,
                    },
                    {
                      id: 'edit',
                      label: t('drivers.actions.editInline', 'Edit inline'),
                      onSelect: helpers.startEdit,
                    },
                    {
                      id: 'delete',
                      label: t('drivers.actions.delete', 'Delete'),
                      onSelect: () => { void handleDelete(row) },
                      destructive: true,
                    },
                  ]}
            />
          )}
        />
      </PageBody>

      <DriverDialog
        open={createOpen}
        onOpenChange={(open) => { if (!createPending) setCreateOpen(open) }}
        mode="create"
        initialValues={toDriverFormValues(null)}
        loading={createPending}
        onSubmit={async (payload: DriverMutationPayload) => {
          setCreatePending(true)
          try {
            await createCrud('road/drivers', payload, {
              errorMessage: t('drivers.messages.createError', 'Failed to create driver.'),
            })
            flash(t('drivers.messages.createSuccess', 'Driver created.'), 'success')
            setCreateOpen(false)
            await refreshDrivers()
          } finally {
            setCreatePending(false)
          }
        }}
      />
      {ConfirmDialogElement}
    </Page>
  )
}
